import React, { useState, useRef, useEffect } from 'react';
import Container from '../Container/Container';
import { useSelector } from 'react-redux';
import { get, post } from '../ApiServices/ApiServices';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Loader from './Component/Loader';

/* ✅ RESTORED: LAST COMPLETED SATURDAY */
function getPriorSaturday() {
    const today = moment();
    const day = today.day();
    const diff = day === 6 ? 7 : day + 1;
    return today.clone().subtract(diff, 'days');
}

function getStartDate(endDate) {
    return moment(endDate, 'MM/DD/YYYY').subtract(11, 'month');
}

function ProcessReturns() {
    const navigate = useNavigate();
    const profile = useSelector((state) => state.profile.profile);

    /* ✅ DEFAULT = LAST SATURDAY */
    const getDefaultEnd = () => getPriorSaturday().format('MM/DD/YYYY');

    const getDefaultStart = (end) =>
        moment(end, 'MM/DD/YYYY').subtract(11, 'month').format('MM/DD/YYYY');

    const [weekEndingDate, setWeekEndingDate] = useState(getDefaultEnd());
    const [startDate, setStartDate] = useState(getDefaultStart(getDefaultEnd()));

    const [weekLabel, setWeekLabel] = useState('');
    const [dateError, setDateError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [historyProcess, setHistoryProcess] = useState([]);

    const confirmModalCloseRef = useRef(null);

    const Getgetprocess = async () => {
        try {
            setIsLoading(true);
            const totalData = await get('getprocessReturnHistory');
            setHistoryProcess(totalData?.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch data");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        Getgetprocess();
    }, []);

    useEffect(() => {
        const end = moment(weekEndingDate, 'MM/DD/YYYY');
        const start = moment(startDate, 'MM/DD/YYYY');
        setWeekLabel(`${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`);
    }, [weekEndingDate, startDate]);

    const handleNo = () => {
        const defaultEnd = getDefaultEnd();
        const defaultStart = getDefaultStart(defaultEnd);

        setWeekEndingDate(defaultEnd);
        setStartDate(defaultStart);
        setDateError('');
        confirmModalCloseRef.current?.click();
    };

    const handleYes = async () => {
        const end = moment(weekEndingDate, 'MM/DD/YYYY', true);
        const start = moment(startDate, 'MM/DD/YYYY', true);

        if (!end.isValid() || !start.isValid()) {
            setDateError('Invalid date format');
            return;
        }

        if (start.isAfter(end)) {
            setDateError('Start date cannot be after end date');
            return;
        }

        setDateError('');
        setIsProcessing(true);
        confirmModalCloseRef.current?.click();

        try {
            const response = await post('processReturns', {
                startDate: start.format('YYYY-MM-DD'),
                endDate: end.format('YYYY-MM-DD'),
            });

            if (response?.status) {
                Getgetprocess();
                toast.success(response?.message);

                const defaultEnd = getDefaultEnd();
                const defaultStart = getDefaultStart(defaultEnd);

                setWeekEndingDate(defaultEnd);
                setStartDate(defaultStart);
            } else {
                toast.error('Processing failed');
            }
        } catch {
            toast.error('Something went wrong');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Container>
            {isProcessing ? <Loader /> :
                <div className="nk-content">
                    <div className="container-fluid">
                        <div className="nk-content-inner">
                            <div className="nk-content-body">

                                <div className="nk-block-head nk-block-head-sm pbZeroHeadin">
                                    <div className="nk-block-between">
                                        <h3 className="nk-block-title page-title">Process Returns</h3>

                                        {profile?.name === 'Admin' && (
                                            <button
                                                className="btn btn-primary"
                                                data-bs-toggle="modal"
                                                data-bs-target="#processReturnsModal"
                                            >
                                                {isProcessing ? 'Processing...' : 'Process Returns'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* HISTORY */}
                                <div className="card card-preview">
                                    <div className="card-inner">
                                        <h6 className="mb-3">Processing History</h6>

                                        <div style={{ border: '1px solid #000' }}>
                                            {isLoading ? <Loader /> : historyProcess.map((entry, idx) => (
                                                <div key={idx} style={{
                                                    display: 'flex',
                                                    borderBottom: idx !== historyProcess.length - 1 ? '1px solid #000' : 'none'
                                                }}>
                                                    <div style={{
                                                        width: '260px',
                                                        padding: '10px',
                                                        borderRight: '1px solid #000'
                                                    }}>
                                                        {moment.utc(entry.processed_at).format('MMMM D, YYYY [at] h:mm a')}
                                                    </div>

                                                    <div style={{ flex: 1, padding: '10px' }}>
                                                        <strong>Summary:</strong>
                                                        <ul style={{ listStyle: 'none', padding: 0 }}>
                                                            <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span>Returned</span>
                                                                <span>{entry.returned_count}</span>
                                                            </li>
                                                            <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span>Not Returned</span>
                                                                <span>{entry.not_returned_count}</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            }

            {/* MODAL */}
            <div className="modal fade" id="processReturnsModal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>Process Returns</h5>
                            <button className="close" data-bs-dismiss="modal" ref={confirmModalCloseRef}>×</button>
                        </div>

                        <div className="modal-body">
                            <p>Do you want to process returns for the date range below?</p>

                            {/* ✅ SINGLE RANGE PICKER */}
                            <div className="col-md-12">
                                <label>Select Date</label>

                                <DateRangePicker
                                    key={startDate + weekEndingDate}
                                    initialSettings={{
                                        startDate: moment(startDate, 'MM/DD/YYYY'),
                                        endDate: moment(weekEndingDate, 'MM/DD/YYYY'),
                                        autoApply: true,
                                        locale: { format: 'MM/DD/YYYY' },
                                    }}
                                    onApply={(e, picker) => {
                                        const newStart = picker.startDate.format('MM/DD/YYYY');
                                        const newEnd = picker.endDate.format('MM/DD/YYYY');

                                        if (moment(newStart).isAfter(moment(newEnd))) {
                                            toast.error("Start date cannot be after end date");
                                            return;
                                        }

                                        setStartDate(newStart);
                                        setWeekEndingDate(newEnd);
                                    }}
                                >
                                    <input
                                        className="form-control"
                                        value={`${moment(startDate, 'MM/DD/YYYY').format('MMM D, YYYY')} - ${moment(weekEndingDate, 'MM/DD/YYYY').format('MMM D, YYYY')}`}
                                        readOnly
                                    />
                                </DateRangePicker>
                            </div>

                            <div className="mt-3">
                                <strong>Selected Range:</strong> {weekLabel}
                            </div>
                            {dateError && <div className="text-danger mt-2">{dateError}</div>}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={handleNo}>No</button>
                            <button className="btn btn-primary" onClick={handleYes}>
                                {isProcessing ? 'Processing...' : 'Yes'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
}

export default ProcessReturns;